import { useMemo, useState } from 'react'
import {
  build,
  evaluateProperty,
  keyframesFor,
  type Easing,
  type KeyframeProperty,
  type Sequence,
  type TimelineItem,
} from '@easyedit/timeline'
import { useEditorStore } from '../../../stores/editorStore'

/** Propriedades que valem uma pista, com o rótulo e a formatação de cada uma. */
const TRACKED: Array<{
  property: KeyframeProperty
  label: string
  format: (value: number) => string
  step: number
  min: number
  max: number
}> = [
  { property: 'transform.scale', label: 'Escala', format: (v) => `${Math.round(v * 100)}%`, step: 0.01, min: 0.1, max: 6 },
  { property: 'transform.x', label: 'Pos. X', format: (v) => `${Math.round(v)}px`, step: 1, min: -4000, max: 4000 },
  { property: 'transform.y', label: 'Pos. Y', format: (v) => `${Math.round(v)}px`, step: 1, min: -4000, max: 4000 },
  { property: 'transform.rotation', label: 'Rotação', format: (v) => `${Math.round(v)}°`, step: 1, min: -360, max: 360 },
  { property: 'transform.opacity', label: 'Opacidade', format: (v) => `${Math.round(v * 100)}%`, step: 0.01, min: 0, max: 1 },
  { property: 'volume', label: 'Volume', format: (v) => `${Math.round(v * 100)}%`, step: 0.01, min: 0, max: 2 },
]

const EASINGS: Easing[] = ['linear', 'ease-in', 'ease-out', 'ease-in-out', 'hold']

export const KEYFRAME_LANE_HEIGHT = 22

/**
 * Pista de keyframes do clip selecionado.
 *
 * Quatro comandos — SET_KEYFRAMES, ADD_KEYFRAME, REMOVE_KEYFRAMES, UPDATE_KEYFRAME —
 * existiam sem nenhuma forma de serem pedidos. A consequência era concreta: o Smart
 * Zoom e o Auto Reframe entregavam curvas que ninguém conseguia ajustar, e a aceleração
 * de cada trecho, que o render agora compila de verdade, não tinha como ser escolhida.
 *
 * A pista aparece só com um clip selecionado — não é uma track da sequência, é uma
 * lente sobre o item. Aparecer sempre ocuparia espaço permanente com informação que na
 * maior parte do tempo não existe.
 */
export function KeyframeLane({
  sequence,
  item,
  zoom,
}: {
  sequence: Sequence
  item: TimelineItem
  zoom: number
}) {
  const dispatch = useEditorStore((s) => s.dispatch)
  const playhead = useEditorStore((s) => s.playhead)
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const ctx = { sequenceId: sequence.id }
  /** Tempo local ao item — é nele que os keyframes vivem. */
  const local = playhead - item.start
  const insideItem = local >= -1e-6 && local <= item.duration + 1e-6

  /**
   * Uma linha por propriedade ANIMADA — não uma por propriedade animável.
   *
   * Seis linhas fixas empurram a timeline inteira para fora da tela num clip que quase
   * sempre tem zero ou uma curva. As que ainda não têm keyframe vivem no menu ao lado,
   * que é onde se pede uma curva nova.
   */
  const lanes = useMemo(
    () => TRACKED.map((entry) => ({ ...entry, keys: keyframesFor(item, entry.property) }))
      .filter((entry) => entry.keys.length > 0),
    [item],
  )

  const available = useMemo(
    () => TRACKED.filter((entry) => relevant(entry.property, item) && keyframesFor(item, entry.property).length === 0),
    [item],
  )

  const selected = lanes
    .flatMap((l) => l.keys.map((k) => ({ key: k, lane: l })))
    .find((e) => e.key.id === selectedId)

  function record(property: KeyframeProperty) {
    if (!insideItem) return
    const existing = keyframesFor(item, property).find((k) => Math.abs(k.time - local) < 0.02)
    if (existing) {
      // Já há keyframe aqui: gravar de novo atualiza o valor para o atual da propriedade.
      void dispatch(
        build.updateKeyframe(ctx, item.id, existing.id, {
          value: evaluateProperty(item, property, local),
        }),
      )
      return
    }
    void dispatch(
      build.addKeyframe(
        ctx,
        item.id,
        build.keyframeAt(property, local, evaluateProperty(item, property, local)),
      ),
    )
  }

  return (
    <div className="border-t border-ink-750 bg-ink-900/60">
      <div className="flex items-center gap-2 px-2 py-1 border-b border-ink-800/60">
        <span className="text-[9px] uppercase tracking-wider text-ink-500">Keyframes</span>
        {available.length > 0 && (
          <select
            value=""
            onChange={(e) => {
              if (e.target.value) record(e.target.value as KeyframeProperty)
              e.target.value = ''
            }}
            disabled={!insideItem}
            title={insideItem ? 'Gravar a primeira curva' : 'Leve o playhead para dentro do clip'}
            className="rounded border border-ink-750 bg-ink-900 px-1.5 py-0.5 text-2xs
              text-ink-300 focus:border-accent focus:outline-none disabled:opacity-40"
          >
            <option value="">+ animar…</option>
            {available.map((entry) => (
              <option key={entry.property} value={entry.property}>{entry.label}</option>
            ))}
          </select>
        )}
        {lanes.length === 0 && (
          <span className="text-2xs text-ink-600">
            Nenhuma curva neste clip. Posicione o playhead e escolha uma propriedade.
          </span>
        )}
      </div>

      {lanes.map((lane) => (
        <div key={lane.property} className="relative border-b border-ink-800/60" style={{ height: KEYFRAME_LANE_HEIGHT }}>
          <button
            onClick={() => record(lane.property)}
            disabled={!insideItem}
            title={
              insideItem
                ? `Gravar keyframe de ${lane.label} no playhead`
                : 'Leve o playhead para dentro do clip'
            }
            className="absolute left-1 top-1/2 -translate-y-1/2 z-10 flex items-center gap-1
              rounded px-1 py-0.5 text-[9px] text-ink-400 hover:text-accent hover:bg-ink-800
              disabled:opacity-30 disabled:hover:text-ink-400 disabled:hover:bg-transparent transition-colors"
          >
            <span className="text-[8px]">◆</span>
            {lane.label}
          </button>

          {lane.keys.map((key) => (
            <button
              key={key.id}
              onClick={() => setSelectedId(key.id === selectedId ? null : key.id)}
              title={`${lane.label} ${lane.format(key.value)} · ${key.easing}`}
              className="absolute top-1/2 z-20 w-2.5 h-2.5 -ml-1.25 -mt-1.25 rotate-45 rounded-[1px] transition-colors"
              style={{
                left: (item.start + key.time) * zoom,
                background: key.id === selectedId ? 'var(--kf-active, #E8A33D)' : '#4FBFA9',
                boxShadow: key.id === selectedId ? '0 0 0 2px rgba(232,163,61,.35)' : undefined,
              }}
            />
          ))}

          {/* A janela do clip, para dar a referência de onde a curva pode existir. */}
          <div
            className="absolute top-0 bottom-0 border-x border-ink-750/60 pointer-events-none"
            style={{ left: item.start * zoom, width: item.duration * zoom }}
          />
        </div>
      ))}

      {selected && (
        <div className="flex items-center gap-2 px-2 py-1.5 border-b border-ink-800 bg-ink-850">
          <span className="text-2xs text-ink-400">{selected.lane.label}</span>
          <input
            type="number"
            value={Number(selected.key.value.toFixed(3))}
            step={selected.lane.step}
            min={selected.lane.min}
            max={selected.lane.max}
            onChange={(e) =>
              void dispatch(
                build.updateKeyframe(ctx, item.id, selected.key.id, { value: Number(e.target.value) }),
                { coalesceKey: `kf:${selected.key.id}:value` },
              )
            }
            className="w-20 rounded border border-ink-750 bg-ink-900 px-1.5 py-0.5
              font-mono text-2xs text-ink-100 tnum focus:border-accent focus:outline-none"
          />
          <span className="font-mono text-2xs text-ink-500 tnum">
            {(item.start + selected.key.time).toFixed(2)}s
          </span>
          <select
            value={selected.key.easing}
            onChange={(e) =>
              void dispatch(
                build.updateKeyframe(ctx, item.id, selected.key.id, { easing: e.target.value as Easing }),
              )
            }
            title="Aceleração deste trecho até o próximo keyframe"
            className="rounded border border-ink-750 bg-ink-900 px-1.5 py-0.5 text-2xs
              text-ink-100 focus:border-accent focus:outline-none"
          >
            {EASINGS.map((e) => (
              <option key={e} value={e}>{e}</option>
            ))}
          </select>
          <button
            onClick={() => {
              void dispatch(build.removeKeyframes(ctx, item.id, [selected.key.id]))
              setSelectedId(null)
            }}
            className="ml-auto btn-ghost text-2xs text-danger"
            title="Remover keyframe"
          >
            Remover
          </button>
        </div>
      )}
    </div>
  )
}

/** A propriedade faz sentido para este tipo de item? */
function relevant(property: KeyframeProperty, item: TimelineItem): boolean {
  if (property === 'volume') return item.kind === 'audio' || item.kind === 'video'
  return item.kind === 'video' || item.kind === 'image' || item.kind === 'text' || item.kind === 'shape'
}

